import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ApiConfig, ApiService } from '../services/types/api.types';
import { tokenManager } from '../utils/tokenManager';
import { ENV } from './environment';

// Event emitter for service unavailable events
type ServiceUnavailableCallback = (service: ApiService, errorData: ServiceUnavailableError) => void;
let serviceUnavailableCallback: ServiceUnavailableCallback | null = null;

type SessionExpiredCallback = () => void;
let sessionExpiredCallback: SessionExpiredCallback | null = null;

export interface ServiceUnavailableError {
  success: boolean;
  errorCode: string;
  serviceName: string;
  message: string;
  retryAfter: number;
}

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

export function onServiceUnavailable(callback: ServiceUnavailableCallback): () => void {
  serviceUnavailableCallback = callback;
  return () => {
    serviceUnavailableCallback = null;
  };
}

export function onSessionExpired(callback: SessionExpiredCallback): () => void {
  sessionExpiredCallback = callback;
  return () => {
    sessionExpiredCallback = null;
  };
}

const API_CONFIGS: Record<ApiService, ApiConfig> = {
  auth: {
    baseURL: ENV.AUTH_SERVICE_URL,
    timeout: ENV.API_TIMEOUT,
  },
  user: {
    baseURL: ENV.USER_SERVICE_URL,
    timeout: ENV.API_TIMEOUT,
  },
  registration: {
    baseURL: ENV.REGISTRATION_SERVICE_URL,
    timeout: ENV.API_TIMEOUT,
  },
  inspection: {
    baseURL: ENV.INSPECTION_SERVICE_URL,
    timeout: ENV.API_TIMEOUT,
  },
  checklist: {
    baseURL: ENV.CHECKLIST_SERVICE_URL,
    timeout: ENV.API_TIMEOUT,
  },
  notification: {
    baseURL: ENV.NOTIFICATION_SERVICE_URL,
    timeout: ENV.API_TIMEOUT,
  },
};

export const createApiClient = (service: ApiService): AxiosInstance => {
  const config = API_CONFIGS[service];

  const client = axios.create({
    baseURL: config.baseURL,
    timeout: config.timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = tokenManager.getAccessToken();
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  client.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Handle 503 Service Unavailable
      if (error.response?.status === 503) {
        const errorData = error.response.data as ServiceUnavailableError;
        if (serviceUnavailableCallback) {
          serviceUnavailableCallback(service, errorData);
        }
        // Don't redirect to login for service unavailable errors
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const requestUrl = originalRequest.url || '';
        const isAuthEndpoint = requestUrl.includes('/login') ||
                               requestUrl.includes('/refresh') ||
                               requestUrl.includes('/password');

        if (isAuthEndpoint) {
          return Promise.reject(error);
        }

        const isOnLoginPage = window.location.pathname === '/login' ||
                              window.location.pathname === '/recover-password' ||
                              window.location.pathname.startsWith('/reset-password');

        if (isOnLoginPage) {
          return Promise.reject(error);
        }

        try {
          const refreshToken = tokenManager.getRefreshToken();
          if (!refreshToken) {
            if (sessionExpiredCallback) {
              sessionExpiredCallback();
            }
            tokenManager.clearTokens();
            window.location.href = '/login';
            return Promise.reject(error);
          }

          if (isRefreshing && refreshPromise) {
            const newAccessToken = await refreshPromise;
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return client(originalRequest);
          }

          isRefreshing = true;
          refreshPromise = (async () => {
            try {
              const refreshResponse = await axios.post(`${API_CONFIGS.auth.baseURL}/refresh`, {
                refreshToken,
              });

              if (!refreshResponse.data.success) {
                throw new Error('Refresh failed');
              }

              const {
                accessToken,
                refreshToken: newRefreshToken,
                expiresIn,
                user,
              } = refreshResponse.data.data;

              tokenManager.setTokens({
                accessToken,
                refreshToken: newRefreshToken,
                expiresIn,
              });

              if (user) {
                // Preservar providerId do user existente se o refresh não retornar
                const existingUser = tokenManager.getUser();
                const mergedUser = {
                  ...existingUser,
                  ...user,
                  // Se o user do refresh não trouxer providerId, manter o existente
                  providerId: user.providerId || existingUser?.providerId,
                };
                tokenManager.setUser(mergedUser);
                // Notifica o AuthContext para sincronizar o React state
                window.dispatchEvent(new Event('user-data-updated'));
              }

              return accessToken as string;
            } finally {
              isRefreshing = false;
            }
          })();

          try {
            const newAccessToken = await refreshPromise;
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return client(originalRequest);
          } finally {
            refreshPromise = null;
          }
        } catch (refreshError) {
          if (sessionExpiredCallback) {
            sessionExpiredCallback();
          }
          tokenManager.clearTokens();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

export const authClient = createApiClient('auth');

export const userClient = createApiClient('user');

export const registrationClient = createApiClient('registration');

export const inspectionClient = createApiClient('inspection');

export const checklistClient = createApiClient('checklist');

export const notificationClient = createApiClient('notification');

export const apiClient = authClient;
